
import { StyleManager } from './utils/styles/StyleManager';
import { UrlResolver } from './utils/UrlResolver';
import { ReactRenderer } from './utils/ReactRenderer';
import { CalculatorContainer } from './CalculatorContainer';
import { CalculatorLifecycle } from './CalculatorLifecycle';
import i18n from '../../i18n';

class CostCalculatorElement extends HTMLElement {
  private shadow: ShadowRoot;
  private container: CalculatorContainer;
  private lifecycle: CalculatorLifecycle;
  private styleManager: StyleManager;
  private urlResolver: UrlResolver;
  private reactRenderer: ReactRenderer;
  private stylesReadyHandler: ((event: Event) => void) | null = null;
  
  constructor() {
    super();
    console.log('[DEBUG] CostCalculatorElement: Initializing web component');
    
    this.shadow = this.attachShadow({ mode: 'open' });
    console.log('[DEBUG] Shadow DOM created with mode:', this.shadow.mode);
    
    this.container = new CalculatorContainer();
    this.shadow.appendChild(this.container.getElement());
    console.log('[DEBUG] Created main container in shadow DOM');
    
    this.lifecycle = new CalculatorLifecycle();
    this.urlResolver = new UrlResolver();
    const baseUrl = this.urlResolver.determineBaseUrl();
    console.log('[DEBUG] CostCalculatorElement initialized with baseUrl:', baseUrl);
    
    this.styleManager = new StyleManager(this.shadow, baseUrl);
    this.reactRenderer = new ReactRenderer(this.container.getElement(), this.urlResolver);
    
    this.stylesReadyHandler = this.handleStylesReady.bind(this);
    console.log('[DEBUG] Event handlers created');
  }
  
  private handleStylesReady(event: Event): void {
    console.log('[DEBUG] Styles loaded event received');
    this.lifecycle.setStylesReady(true);
    this.tryRender();
  }
  
  private tryRender(): void {
    console.log('[DEBUG] tryRender called, styles ready:', this.lifecycle.isStylesReady(), 'initialized:', this.lifecycle.isInitialized());
    
    if (this.lifecycle.isStylesReady() && !this.lifecycle.isInitialized()) {
      console.log('[DEBUG] Styles are ready, rendering React component');
      console.log('[DEBUG] Adding logo first');
      this.reactRenderer.addLogo();
      
      console.log('[DEBUG] Waiting 100ms before attempting React render');
      setTimeout(() => {
        console.log('[DEBUG] Timeout elapsed, rendering React component now');
        try {
          this.reactRenderer.render();
          console.log('[DEBUG] CostCalculatorElement: Component fully rendered');
          this.lifecycle.setInitialized(true);
          
          // Clear the render timeout since we're done
          if (this.lifecycle.getRenderTimeout() !== null) {
            window.clearTimeout(this.lifecycle.getRenderTimeout()!);
            this.lifecycle.setRenderTimeout(null);
            console.log('[DEBUG] Cleared render timeout');
          }
        } catch (error) {
          console.error('[ERROR] Error in tryRender:', error);
          this.handleError();
        }
      }, 100);
    }
  }
  
  connectedCallback(): void {
    console.log('[DEBUG] CostCalculatorElement: Connected to DOM');
    
    if (!this.isConnected) {
      console.warn('[WARN] Element is not connected to DOM during connectedCallback');
      setTimeout(() => this.connectedCallback(), 100);
      return;
    }
    
    const lang = this.getAttribute('language') || 'en';
    console.log('[DEBUG] Language set to:', lang);
    if (lang && (lang === 'de' || lang === 'en')) {
      i18n.changeLanguage(lang);
    }
    
    console.log('[DEBUG] CostCalculatorElement connected with baseUrl:', this.urlResolver.getBaseUrl());
    
    // Add the styles-loaded event listener
    console.log('[DEBUG] Adding styles-loaded event listener');
    if (this.stylesReadyHandler) {
      this.shadow.addEventListener('onexip-styles-loaded', this.stylesReadyHandler);
    }
    
    // Add all the styles
    console.log('[DEBUG] Adding styles via StyleManager');
    this.styleManager.addAllStyles();
    
    // Set a backup render timeout
    console.log('[DEBUG] Setting 3 second render timeout as backup');
    this.lifecycle.setRenderTimeout(window.setTimeout(() => {
      if (!this.lifecycle.isStylesReady()) {
        console.warn('[WARN] Styles not loaded after timeout, rendering with base styles');
        this.lifecycle.setStylesReady(true);
        this.tryRender();
      }
    }, 3000));
  }
  
  disconnectedCallback(): void {
    this.cleanup();
  }
  
  private cleanup(): void {
    if (this.stylesReadyHandler) {
      this.shadow.removeEventListener('onexip-styles-loaded', this.stylesReadyHandler);
      this.stylesReadyHandler = null;
    }
    
    this.lifecycle.cleanup();
    this.reactRenderer.cleanup();
  }
  
  private handleError(): void {
    this.container.reset();
    const errorDiv = document.createElement('div');
    errorDiv.innerHTML = `
      <div style="padding: 20px; text-align: center; font-family: sans-serif;">
        <h2 style="color: #333;">Cost Calculator</h2>
        <p style="color: #666;">There was an error loading the calculator. Please refresh the page or contact support.</p>
      </div>
    `;
    this.container.getElement().appendChild(errorDiv);
  }
  
  static get observedAttributes() { 
    return ['language']; 
  }
  
  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    console.log('[DEBUG] Attribute changed:', name, 'from', oldValue, 'to', newValue);
    if (name === 'language' && oldValue !== newValue) {
      console.log('[DEBUG] Language changed to', newValue);
      if (newValue === 'de' || newValue === 'en') {
        i18n.changeLanguage(newValue);
      }
    }
  }
}

// Register the web component
console.log('[DEBUG] Registering cost-calculator web component');
customElements.define('cost-calculator', CostCalculatorElement);
console.log('[DEBUG] cost-calculator web component registered successfully');

export default CostCalculatorElement;
